import React, { useState, useEffect } from 'react';
import api from '../../api';

const HalloweenShopModal = ({ 
  isOpen, 
  toggle, 
  spawnText,
  playerPos,
  shopId
}) => {
  // State management
  const [loading, setLoading] = useState(false);
  const [shopInfo, setShopInfo] = useState(null);
  const [shopItems, setShopItems] = useState([]);
  const [pokemonItems, setPokemonItems] = useState([]);
  const [loadingShop, setLoadingShop] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [currency, setCurrency] = useState({ halloweenCandy: 0 });
  const [activeTab, setActiveTab] = useState('pokemon');

  // Track if data has been loaded to prevent redundant API calls
  const [dataLoaded, setDataLoaded] = useState(false);

  // Load data when modal opens
  useEffect(() => {
    // Only fetch data when modal opens and data hasn't been loaded yet
    if (!isOpen) {
      return;
    }
    
    // Skip fetching if we already loaded the data for this shop
    if (dataLoaded && shopInfo) {
      return;
    }

    const fetchEventShop = async () => {
      try {
        setLoadingShop(true);
        const response = await api.get(`/v1/event-shop/${shopId}`, { withCredentials: true });
        setShopInfo(response.data.shopInfo);
        setPokemonItems(response.data.pokemon || []);
        setShopItems(response.data.items || []);
        
        // Get currency (Halloween Candy)
        const inventoryRes = await api.get('/v1/inventory', { withCredentials: true });
        
        // Find Halloween Candy (item 130) in inventory
        const halloweenCandy = inventoryRes.data.find(item => item.item === 130) || { amount: 0, item_image: 'items/halloweencandy.png' };
        
        setCurrency({
          halloweenCandy: halloweenCandy.amount || 0,
          halloweenCandyImage: halloweenCandy.item_image ? `/${halloweenCandy.item_image}` : '/items/HalloweenCandy.png'
        });
        
        // Mark data as loaded to prevent redundant API calls
        setDataLoaded(true);
      } catch (err) {
        console.error('Error loading event shop:', err);
        if (spawnText && playerPos) {
          spawnText('Failed to load event shop', playerPos.x, playerPos.y - 20, '#f87171');
        }
      } finally {
        setLoadingShop(false);
      }
    };

    fetchEventShop();
  }, [isOpen, shopId, dataLoaded, shopInfo, playerPos, spawnText]);

  const handlePurchase = async (item, isPokemon = false) => {
    setErrorMessage(null);
    if (!item) return;
    
    // If this is a Marshadow, we need to randomly select between Ruby and Sapphire variants
    let purchaseItemId = item.id;
    
    if (isPokemon && item.name.includes('Marshadow')) {
      // Find all Marshadow variants
      const marshadowVariants = pokemonItems.filter(p => p.name.includes('Marshadow'));
      
      if (marshadowVariants.length > 1) {
        // Randomly select one of the variants
        const randomIndex = Math.floor(Math.random() * marshadowVariants.length);
        purchaseItemId = marshadowVariants[randomIndex].id;
        console.log(`Randomly selected ${marshadowVariants[randomIndex].name} (ID: ${purchaseItemId})`);
      }
    }

    try {
      setLoading(true);
      const response = await api.post(
        '/v1/event-shop/purchase',
        { 
          shop_id: shopId, 
          item_id: purchaseItemId, // Use the potentially randomized ID
          is_pokemon: isPokemon,
          currency_type: 'halloween_candy' // Specify Halloween candy as currency
        },
        { withCredentials: true }
      );
      
      // Update currency immediately
      try {
        // Get updated inventory for currency
        const inventoryRes = await api.get('/v1/inventory', { withCredentials: true });
        const halloweenCandy = inventoryRes.data.find(item => item.item === 130) || { amount: 0, item_image: 'items/halloweencandy.png' };
        
        setCurrency({
          halloweenCandy: halloweenCandy.amount || 0,
          halloweenCandyImage: halloweenCandy.item_image ? `/${halloweenCandy.item_image}` : '/items/HalloweenCandy.png'
        });
      } catch (refreshErr) {
        console.error('Error refreshing currency data:', refreshErr);
      }
      
      // Get the actual Pokémon received (for Marshadow variants)
      let actualPokemonReceived = item.name;
      if (isPokemon && response.data && response.data.pokemon_received) {
        actualPokemonReceived = response.data.pokemon_received;
      }
      
      // Show success message with floating text
      if (spawnText && playerPos) {
        const displayName = isPokemon ? 
          (item.name.includes('Marshadow') ? actualPokemonReceived : item.name) : 
          item.item_name;
          
        const itemImage = isPokemon ? 
          `/sprites/${actualPokemonReceived}.png` : 
          (item.image ? `/${item.image}` : `/items/${item.item_name.replace(/ /g, '')}.png`);
        
        // Show item gained with a slight delay to ensure it appears
        setTimeout(() => {
          spawnText(`Obtained ${displayName}!`, playerPos.x, playerPos.y - 20, '#4ade80', itemImage || null);
          
          // Show candy spent with a further delay
          setTimeout(() => {
            spawnText(`-${item.price} Halloween Candy`, playerPos.x, playerPos.y - 40, '#f87171', currency.halloweenCandyImage);
          }, 300);
        }, 100);
      }
      
      // Update shop data to reflect purchase counts - do this only once
      try {
        const shopRes = await api.get(`/v1/event-shop/${shopId}`, { withCredentials: true });
        setPokemonItems(shopRes.data.pokemon || []);
        setShopItems(shopRes.data.items || []);
      } catch (shopErr) {
        console.error('Error refreshing shop data:', shopErr);
      }
      
      // Close the modal after successful purchase with enough delay for text to be seen
      setTimeout(() => {
        toggle();
      }, 1500);
      
    } catch (err) {
      setErrorMessage(err.response?.data?.detail || 'Purchase failed');
      if (spawnText && playerPos) {
        spawnText(err.response?.data?.detail || 'Purchase failed', playerPos.x, playerPos.y - 20, '#f87171');
      }
    } finally {
      setLoading(false);
    }
  };

  const canAffordItem = (item) => {
    if (!currency) return false;
    
    // Check if user has reached their purchase limit
    const remainingPurchases = Math.max(0, item.max_purchases - item.purchased_count);
    if (remainingPurchases <= 0 && item.max_purchases > 0) return false;
    
    // Check if user has enough Halloween Candy
    return currency.halloweenCandy >= item.price;
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px 10px',
      boxSizing: 'border-box',
      zIndex: 1050
    }}>
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '900px',
          maxHeight: 'calc(100vh - 40px)',
          background: 'rgba(29, 34, 46, 0.95)',
          color: '#fff',
          borderRadius: '12px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 165, 0, 0.3)', // Orange border for Halloween
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid rgba(255, 165, 0, 0.3)',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#ff6600' }}>
              {shopInfo ? shopInfo.name : 'Witchveil Market'}
            </h3>
          </div>
          <button
            onClick={toggle}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'transparent',
              border: 'none',
              color: '#ff6600',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 102, 0, 0.1)'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid rgba(255, 165, 0, 0.3)',
          background: 'rgba(0, 0, 0, 0.1)'
        }}>
          <button
            onClick={() => setActiveTab('pokemon')}
            style={{
              padding: '12px 20px',
              background: activeTab === 'pokemon' ? 'rgba(255, 102, 0, 0.3)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'pokemon' ? '2px solid #ff6600' : 'none',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'pokemon' ? 'bold' : 'normal'
            }}
          >
            Pokémon
          </button>
          <button
            onClick={() => setActiveTab('items')}
            style={{
              padding: '12px 20px',
              background: activeTab === 'items' ? 'rgba(255, 102, 0, 0.3)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'items' ? '2px solid #ff6600' : 'none',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'items' ? 'bold' : 'normal'
            }}
          >
            Items
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          padding: '20px',
          overflowY: 'auto'
        }}>
          {errorMessage && (
            <div style={{
              marginBottom: 16,
              padding: '8px 12px',
              background: 'rgba(255,0,0,0.1)',
              color: '#f87171',
              borderRadius: 4,
              textAlign: 'center',
              fontSize: 14,
            }}>
              {errorMessage}
            </div>
          )}

          {/* Description */}
          <div style={{
            background: 'rgba(0, 0, 0, 0.3)',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid rgba(255, 165, 0, 0.3)'
          }}>
            <h4 style={{ color: '#ff6600', marginBottom: '8px', fontSize: '14px' }}>
              {shopInfo ? shopInfo.name : 'Witchveil Market'}
            </h4>
            <p style={{ color: '#ccc', fontSize: '12px', margin: 0 }}>
              {shopInfo ? shopInfo.description : 'Exchange your Halloween candy for special event Pokémon and items!'}
            </p>
          </div>

          {/* Loading States */}
          {loadingShop && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#ccc'
            }}>
              Loading shop data...
            </div>
          )}

          {/* Pokemon Tab */}
          {activeTab === 'pokemon' && !loadingShop && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {pokemonItems.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#ccc'
                }}>
                  No Pokémon available at this time.
                </div>
              ) : (
                // Filter and transform the items for display
                pokemonItems
                  .filter(item => {
                    // Only show SapphireMarshadow if no RubyMarshadow exists
                    if (item.name === 'SapphireMarshadow') {
                      return !pokemonItems.some(p => p.name === 'RubyMarshadow');
                    }
                    return true;
                  })
                  .map(item => {
                    const canAfford = canAffordItem(item);
                    const remainingPurchases = Math.max(0, item.max_purchases - item.purchased_count);
                    const isMarshadow = item.name.includes('Marshadow');
                    
                    // If this is RubyMarshadow, find SapphireMarshadow to combine purchase counts
                    let combinedPurchasedCount = item.purchased_count || 0;
                    let combinedMaxPurchases = 2; // Hard-coded total limit of 2 across both variants
                    
                    if (isMarshadow) {
                      // Find all Marshadow variants
                      pokemonItems.forEach(p => {
                        if (p.name.includes('Marshadow') && p.id !== item.id) {
                          combinedPurchasedCount += (p.purchased_count || 0);
                          // Note: We're not adding max_purchases anymore since we hard-coded the limit to 2
                        }
                      });
                    }
                    
                    // Use combined counts for Marshadow
                    const displayPurchasedCount = isMarshadow ? combinedPurchasedCount : item.purchased_count;
                    const displayMaxPurchases = isMarshadow ? combinedMaxPurchases : item.max_purchases;
                    const displayRemainingPurchases = Math.max(0, displayMaxPurchases - displayPurchasedCount);

                    return (
                      <div key={item.id} style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 165, 0, 0.3)',
                        display: 'flex',
                        gap: '20px',
                        alignItems: 'center'
                      }}>
                      {/* Pokemon Sprites */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: '140px'
                      }}>
                        {item.name.includes('Marshadow') ? (
                          <div style={{
                            width: 100,
                            height: 100,
                            background: 'rgba(255, 165, 0, 0.2)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '60px',
                            fontWeight: 'bold',
                            color: '#ff6600',
                            textShadow: '0 0 10px rgba(255, 102, 0, 0.7)',
                            border: '2px solid rgba(255, 165, 0, 0.5)'
                          }}>
                            ?
                          </div>
                        ) : (
                          <img
                            src={`/sprites/${item.name}.png`}
                            alt={item.name}
                            width={100}
                            height={100}
                            style={{ imageRendering: 'pixelated' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                      </div>

                      {/* Pokemon Details */}
                      <div style={{ flex: 1 }}>
                        <h5 style={{ color: '#fff', marginBottom: '12px', fontSize: '16px' }}>
                          {item.name.includes('Marshadow') ? 'Mystery Pokémon (???)' : item.name}
                        </h5>

                        {/* Limits */}
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                          gap: '8px',
                          marginBottom: '16px',
                          fontSize: '11px'
                        }}>
                          <div style={{ color: '#ccc' }}>
                            <strong style={{ color: '#fff' }}>Level:</strong> {item.level || 5}
                          </div>
                          <div style={{ color: '#ccc' }}>
                            <strong style={{ color: '#fff' }}>You've Purchased:</strong> {item.name.includes('Marshadow') ? displayPurchasedCount : (item.purchased_count || 0)}
                          </div>
                          <div style={{ color: '#ccc' }}>
                            <strong style={{ color: '#fff' }}>Can Still Purchase:</strong> {item.name.includes('Marshadow') ? displayRemainingPurchases : remainingPurchases}
                          </div>
                        </div>

                        {/* Costs */}
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: '16px',
                          marginBottom: '16px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: 'rgba(255, 165, 0, 0.15)',
                            padding: '6px 10px',
                            borderRadius: '4px'
                          }}>
                            <img
                              src={currency.halloweenCandyImage || "/items/HalloweenCandy.png"}
                              alt="Halloween Candy"
                              width={16}
                              height={16}
                            />
                            <span style={{ fontSize: '12px', color: '#fff' }}>
                              {item.price} Halloween Candy
                            </span>
                          </div>
                        </div>

                        {/* Purchase Button */}
                        <button
                          onClick={() => handlePurchase(item, true)}
                          disabled={!canAfford || loading || ((item.name.includes('Marshadow') ? displayRemainingPurchases <= 0 : remainingPurchases <= 0) && item.max_purchases > 0)}
                          style={{
                            background: canAfford && (item.max_purchases === 0 || (item.name.includes('Marshadow') ? displayRemainingPurchases > 0 : remainingPurchases > 0)) ? 'rgba(255, 102, 0, 0.8)' : 'rgba(108, 117, 125, 0.6)',
                            border: canAfford && (item.max_purchases === 0 || (item.name.includes('Marshadow') ? displayRemainingPurchases > 0 : remainingPurchases > 0)) ? '1px solid rgba(255, 102, 0, 0.8)' : '1px solid rgba(108, 117, 125, 0.4)',
                            color: '#fff',
                            padding: '10px 20px',
                            borderRadius: '6px',
                            cursor: canAfford && !loading && (item.max_purchases === 0 || (item.name.includes('Marshadow') ? displayRemainingPurchases > 0 : remainingPurchases > 0)) ? 'pointer' : 'not-allowed',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            transition: 'all 0.2s ease',
                            opacity: canAfford && !loading && (item.max_purchases === 0 || (item.name.includes('Marshadow') ? displayRemainingPurchases > 0 : remainingPurchases > 0)) ? 1 : 0.6
                          }}
                          onMouseEnter={(e) => {
                            if (canAfford && !loading && (item.max_purchases === 0 || (item.name.includes('Marshadow') ? displayRemainingPurchases > 0 : remainingPurchases > 0))) {
                              e.target.style.background = 'rgba(255, 102, 0, 1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (canAfford && !loading && (item.max_purchases === 0 || (item.name.includes('Marshadow') ? displayRemainingPurchases > 0 : remainingPurchases > 0))) {
                              e.target.style.background = 'rgba(255, 102, 0, 0.8)';
                            }
                          }}
                        >
                          {loading ? 'Processing...' : 
                           (item.max_purchases > 0 && (item.name.includes('Marshadow') ? displayRemainingPurchases <= 0 : remainingPurchases <= 0)) ? 'Purchase Limit Reached' :
                           canAfford ? 'Purchase Pokémon' : 'Not Enough Halloween Candy'}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Items Tab */}
          {activeTab === 'items' && !loadingShop && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {shopItems.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#ccc'
                }}>
                  No items available at this time.
                </div>
              ) : (
                shopItems.map(item => {
                  const canAfford = canAffordItem(item);
                  const remainingPurchases = Math.max(0, item.max_purchases - item.purchased_count);

                  return (
                    <div key={item.id} style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 165, 0, 0.3)',
                      display: 'flex',
                      gap: '20px',
                      alignItems: 'center'
                    }}>
                      {/* Item Image */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: '80px'
                      }}>
                        <img
                          src={item.image ? `/${item.image}` : `/items/${item.item_name.replace(/ /g, '')}.png`}
                          alt={item.item_name}
                          width={50}
                          height={50}
                          style={{ imageRendering: 'pixelated' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>

                      {/* Item Details */}
                      <div style={{ flex: 1 }}>
                        <h5 style={{ color: '#fff', marginBottom: '12px', fontSize: '16px' }}>
                          {item.item_name}
                        </h5>
                        
                        <p style={{ color: '#ccc', fontSize: '12px', marginBottom: '12px' }}>
                          {item.description || 'No description available.'}
                        </p>

                        {/* Limits */}
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                          gap: '8px',
                          marginBottom: '16px',
                          fontSize: '11px'
                        }}>
                          <div style={{ color: '#ccc' }}>
                            <strong style={{ color: '#fff' }}>You've Purchased:</strong> {item.purchased_count || 0}
                          </div>
                          {item.max_purchases > 0 && (
                            <div style={{ color: '#ccc' }}>
                              <strong style={{ color: '#fff' }}>Can Still Purchase:</strong> {remainingPurchases}
                            </div>
                          )}
                          {item.max_purchases === 0 && (
                            <div style={{ color: '#ccc' }}>
                              <strong style={{ color: '#fff' }}>Purchase Limit:</strong> Unlimited
                            </div>
                          )}
                        </div>

                        {/* Costs */}
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: '16px',
                          marginBottom: '16px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: 'rgba(255, 165, 0, 0.15)',
                            padding: '6px 10px',
                            borderRadius: '4px'
                          }}>
                            <img
                              src={currency.halloweenCandyImage || "/items/HalloweenCandy.png"}
                              alt="Halloween Candy"
                              width={16}
                              height={16}
                            />
                            <span style={{ fontSize: '12px', color: '#fff' }}>
                              {item.price} Halloween Candy
                            </span>
                          </div>
                        </div>

                        {/* Purchase Button */}
                        <button
                          onClick={() => handlePurchase(item, false)}
                          disabled={!canAfford || loading || (item.max_purchases > 0 && remainingPurchases <= 0)}
                          style={{
                            background: canAfford && (item.max_purchases === 0 || remainingPurchases > 0) ? 'rgba(255, 102, 0, 0.8)' : 'rgba(108, 117, 125, 0.6)',
                            border: canAfford && (item.max_purchases === 0 || remainingPurchases > 0) ? '1px solid rgba(255, 102, 0, 0.8)' : '1px solid rgba(108, 117, 125, 0.4)',
                            color: '#fff',
                            padding: '10px 20px',
                            borderRadius: '6px',
                            cursor: canAfford && !loading && (item.max_purchases === 0 || remainingPurchases > 0) ? 'pointer' : 'not-allowed',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            transition: 'all 0.2s ease',
                            opacity: canAfford && !loading && (item.max_purchases === 0 || remainingPurchases > 0) ? 1 : 0.6
                          }}
                          onMouseEnter={(e) => {
                            if (canAfford && !loading && (item.max_purchases === 0 || remainingPurchases > 0)) {
                              e.target.style.background = 'rgba(255, 102, 0, 1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (canAfford && !loading && (item.max_purchases === 0 || remainingPurchases > 0)) {
                              e.target.style.background = 'rgba(255, 102, 0, 0.8)';
                            }
                          }}
                        >
                          {loading ? 'Processing...' : 
                           (item.max_purchases > 0 && remainingPurchases <= 0) ? 'Purchase Limit Reached' :
                           canAfford ? 'Purchase Item' : 'Not Enough Halloween Candy'}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Currency Display */}
          <div style={{
            background: 'rgba(0, 0, 0, 0.3)',
            padding: '16px',
            borderRadius: '8px',
            marginTop: '20px',
            border: '1px solid rgba(255, 165, 0, 0.3)'
          }}>
            <h4 style={{ color: '#ff6600', marginBottom: '12px', fontSize: '14px' }}>Your Currency:</h4>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '12px' 
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'rgba(255, 165, 0, 0.15)',
                padding: '6px 10px',
                borderRadius: '4px'
              }}>
                <img
                  src={currency.halloweenCandyImage || "/items/HalloweenCandy.png"}
                  alt="Halloween Candy"
                  width={16}
                  height={16}
                />
                <span style={{ fontSize: '12px', color: '#fff' }}>
                  {currency.halloweenCandy} Halloween Candy
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HalloweenShopModal;
